import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import {
  fetchUserServiceCommissions,
  createUserServiceCommission,
  updateUserServiceCommission,
  deleteUserServiceCommission,
} from "./thunks";
import { toast } from "react-toastify";

interface UserServiceCommission {
  id: number;
  user_id: number;
  service_id: number;
  commission: number;
  user?: {
    id: number;
    name: string;
  };
  service?: {
    id: number;
    name: string;
  };
}

interface PaginatedUserServiceCommissions {
  data: UserServiceCommission[];
  total?: number;
  currentPage?: number;
  perPage?: number;
}

interface UserServiceCommissionState {
  userServiceCommissions: PaginatedUserServiceCommissions;
  loading: boolean;
  error: string | null;
  fetched: boolean;
}

const initialState: UserServiceCommissionState = {
  userServiceCommissions: { data: [] },
  loading: false,
  error: null,
  fetched: false,
};

const userServiceCommissionSlice = createSlice({
  name: "userServiceCommission",
  initialState,
  reducers: {
    clearUserServiceCommissions: (state) => {
      state.userServiceCommissions = { data: [] };
      state.loading = false;
      state.error = null;
    },
    resetFetched: (state) => {
      state.fetched = false;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchUserServiceCommissions.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(
        fetchUserServiceCommissions.fulfilled,
        (state, action: PayloadAction<any>) => {
          state.loading = false;
          state.fetched = true;
          const payload = action.payload;

          if (Array.isArray(payload.data)) {
            state.userServiceCommissions = { data: payload.data };
          } else {
            state.userServiceCommissions = {
              data: payload.data.data,
              total: payload.data.total,
              currentPage: payload.data.current_page,
              perPage: payload.data.per_page,
            };
          }
        },
      )
      .addCase(fetchUserServiceCommissions.rejected, (state, action) => {
        const payload = action.payload as { error: string };
        state.error = payload.error;
        state.loading = false;
        state.fetched = false;
        toast.error(
          payload.error || "Erro ao carregar comissões personalizadas",
        );
      })

      // Create
      .addCase(createUserServiceCommission.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(
        createUserServiceCommission.fulfilled,
        (state, action: PayloadAction<any>) => {
          state.loading = false;
          state.userServiceCommissions.data.push(action.payload.data);
        },
      )
      .addCase(createUserServiceCommission.rejected, (state, action) => {
        const payload = action.payload as { error: string };
        state.error = payload.error;
        state.loading = false;
        toast.error(payload.error || "Erro ao criar comissão personalizada");
      })

      // Update
      .addCase(updateUserServiceCommission.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(
        updateUserServiceCommission.fulfilled,
        (state, action: PayloadAction<any>) => {
          state.loading = false;
          const updatedCommission = action.payload.data;
          const index = state.userServiceCommissions.data.findIndex(
            (commission) => commission.id === updatedCommission.id,
          );
          if (index !== -1) {
            state.userServiceCommissions.data[index] = updatedCommission;
          }
        },
      )
      .addCase(updateUserServiceCommission.rejected, (state, action) => {
        const payload = action.payload as { error: string };
        state.error = payload.error;
        state.loading = false;
        toast.error(
          payload.error || "Erro ao atualizar comissão personalizada",
        );
      })

      // Delete
      .addCase(deleteUserServiceCommission.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(deleteUserServiceCommission.fulfilled, (state, action) => {
        state.loading = false;
        const deletedId = action.meta.arg;
        state.userServiceCommissions.data =
          state.userServiceCommissions.data.filter(
            (commission) => commission.id !== deletedId,
          );
        toast.success("Comissão diferenciada excluída com sucesso!");
      })
      .addCase(deleteUserServiceCommission.rejected, (state, action) => {
        const payload = action.payload as { error: string };
        state.error = payload.error;
        state.loading = false;
        toast.error(payload.error || "Erro ao excluir comissão personalizada");
      });
  },
});

export const { clearUserServiceCommissions, resetFetched } =
  userServiceCommissionSlice.actions;
export default userServiceCommissionSlice.reducer;
